<template>
  <div class="max-w-4xl mx-auto p-6">
    <!-- Dialog for adding users -->
    <v-dialog persistent v-model="addTransactionDialog" max-width="600">
      <v-card>
        <v-container>
          <v-card-title class="d-flex align-center justify-space-between">
            Transaction Details
            <v-btn icon small @click="closeTransactionDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <!-- SnappyExchange username Selection -->
            <div v-if="withdrawType === 'snappy_exchange_username'">
              <v-text-field
                v-model="snappyExchangeUsername"
                label="Enter your SnappyExchange username"
                filled
                rounded
                :loading="loadingRecipients"
                :hint="
                  hasSearched && recipient
                    ? 'User found'
                    : 'Enter your SnappyExchange username'
                "
                :persistent-hint="hasSearched && !!recipient"
                @keyup.enter="recipientSearch"
              ></v-text-field>
              <v-btn
                color="primary"
                block
                class="mt-2"
                :disabled="!snappyExchangeUsername || loadingRecipients"
                :loading="loadingRecipients"
                @click="recipientSearch"
                >Search</v-btn
              >
              <p
                v-if="hasSearched && !recipient && !loadingRecipients"
                class="text-sm text-red-600 mt-1"
              >
                Username not found
              </p>
            </div>

            <div>
              <div
                v-if="recipient"
                class="bg-gray-100 border border-gray-200 p-4 rounded-lg mt-4"
              >
                <p class="font-semibold text-gray-800 mb-0">
                  {{ recipient.fname }} {{ recipient.lname }}
                </p>
                <p class="text-gray-500 mb-0">{{ recipient.email }}</p>
                <p class="text-gray-500 mb-0">{{ recipient.country }}</p>
              </div>
            </div>

            <div class="mt-6" v-if="recipient">
              <v-text-field
                v-model="amount"
                label="Enter amount to withdraw"
                filled
                rounded
                type="number"
                :suffix="selectedAsset"
                :error-messages="amountError"
                :hint="`You have ${presentCoinData.balance} ${selectedAsset} in your wallet`"
                :persistent-hint="true"
              ></v-text-field>
              <div class="d-flex gap-2 mt-2">
                <v-btn
                  v-for="pct in [25, 50, 75, 100]"
                  :key="pct"
                  small
                  rounded
                  class="flex-grow-1"
                  @click="setAmountPercent(pct)"
                  >{{ pct }}%</v-btn
                >
              </div>
            </div>

            <div class="mt-6" v-if="recipient">
              <v-textarea
                v-model="note"
                label="Enter note"
                placeholder="Enter note (optional)"
                filled
                rounded
              ></v-textarea>
            </div>
          </v-card-text>

          <div class="d-flex gap-2">
            <v-btn
              v-if="recipient && amount"
              block
              color="primary"
              class="d-block mx-auto"
              :disabled="!!amountError"
              @click="addTransaction"
              >Add Transaction
            </v-btn>
            <v-btn
              block
              text
              class="d-block mx-auto"
              @click="closeTransactionDialog"
              >Cancel
            </v-btn>
          </div>
        </v-container>
      </v-card>
    </v-dialog>
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Withdraw</h1>

      <!-- Navigation/Tab Bar -->
      <div class="flex items-center bg-yellow-100 rounded-lg p-2 mb-6">
        <v-btn icon small class="mr-3" @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-gray-800 font-medium">Withdraw Crypto</span>
      </div>
    </div>

    <!-- Section Title and Instructions -->
    <div class="mb-8">
      <h2 class="text-xl font-semibold text-gray-800 mb-2">Withdraw crypto</h2>
      <p class="text-gray-600">
        Please enter the details below to withdraw crypto from your wallet.
      </p>
    </div>

    <!-- Selected coin + available balance -->
    <div
      v-if="presentCoinData && presentCoinData.coin"
      class="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3"
    >
      <img
        v-if="presentCoinData.coin.image"
        :src="presentCoinData.coin.image"
        :alt="presentCoinData.coin.symbol"
        class="w-8 h-8 rounded-full"
      />
      <div>
        <div class="font-semibold text-gray-800">
          {{ presentCoinData.coin.name }} ({{ presentCoinData.coin.symbol }})
        </div>
        <div class="text-sm text-gray-500">
          Available balance:
          <span class="font-medium text-gray-800"
            >{{ formatCryptoAmount(presentCoinData.balance) }}
            {{ presentCoinData.coin.symbol }}</span
          >
        </div>
      </div>
    </div>

    <p v-if="!withdrawIsEnabled" class="text-sm text-red-600 mb-6">
      Withdrawals are currently disabled for {{ selectedAsset }}.
    </p>

    <!-- Withdraw method toggle -->
    <div class="flex gap-2 mb-8">
      <button
        type="button"
        class="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
        :class="
          withdrawType === 'external_wallet'
            ? 'bg-yellow-500 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        "
        @click="withdrawType = 'external_wallet'"
      >
        Withdraw to external wallet
      </button>
      <button
        type="button"
        class="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
        :class="
          withdrawType === 'snappy_exchange_username'
            ? 'bg-yellow-500 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        "
        @click="onSelectSendToUser"
      >
        Send to SnappyExchange user
      </button>
    </div>

    <!-- Input Fields -->
    <div class="">
      <div
        v-if="
          transactions.length > 0 && withdrawType === 'snappy_exchange_username'
        "
      >
        <div v-for="(transaction, index) in transactions" :key="index">
          <div class="mb-4 p-4">
            <div
              class="bg-gray-100 border border-gray-200 shadow-sm rounded-lg flex items-center justify-between"
            >
              <div>
                <p class="font-semibold text-gray-800 mb-0">
                  {{ transaction.recipient.fname }}
                </p>
                <p class="text-gray-500 mb-0 text-sm">
                  {{ transaction.username }}
                </p>
                <p class="text-gray-500 mb-0 text-sm">
                  {{ transaction.recipient.email }}
                </p>
              </div>
              <div>
                <p class="text-gray-500 mb-0 text-base">
                  {{ transaction.amount }} {{ selectedAsset }}
                </p>
              </div>
            </div>
            <p v-if="transaction.note" class="text-gray-500 mb-0 text-sm">
              Note: {{ transaction.note }}
            </p>
            <div class="flex items-center justify-end gap-2 mt-2">
              <v-btn
                color="primary"
                @click="onEditTransaction(transaction, index)"
                :disabled="loadingWithdraw"
                >Edit</v-btn
              >
              <v-btn
                color="error"
                @click="removeTransaction(index)"
                :disabled="loadingWithdraw"
                >Remove</v-btn
              >
            </div>
          </div>
        </div>

        <div class="mt-6">
          <v-btn
            color="primary"
            @click="addMoreTransaction"
            :disabled="transactions.length === 0 || loadingWithdraw"
            >Add More recipients</v-btn
          >
        </div>
      </div>

      <div class="mt-6" v-if="recipient">
        <v-text-field
          v-model="otp"
          filled
          rounded
          label="OTP"
          hint="An OTP will be sent to your registered email to approve this transaction"
          persistent-hint
        >
          <template v-slot:append-outer>
            <v-btn :loading="sendingOtp" @click="generateOtp" color="primary" dark>
              Request OTP
            </v-btn>
          </template>
        </v-text-field>
        <v-btn
          color="primary"
          size="large"
          class="mt-4"
          :disabled="transactions.length === 0 || !withdrawIsEnabled || !otp"
          @click="withdrawCrypto"
          :loading="loadingWithdraw"
        >
          Withdraw
        </v-btn>
      </div>
    </div>

    <!-- Add fields to select network, enter amount, enter receiver wallet address, enter optional remark -->
    <div v-if="withdrawType === 'external_wallet'">
      <div class="mt-6">
        <v-select
          v-model="selectedNetwork"
          :items="availableNetworks"
          item-text="name"
          item-value="_id"
          label="Select Network"
          filled
          rounded
          placeholder="Select network"
          :loading="!presentCoinData"
          :disabled="!presentCoinData"
        ></v-select>
        <!-- No network on the wallet yet: say so instead of a dead select -->
        <p
          v-if="presentCoinData && availableNetworks.length === 0"
          class="text-sm text-red-600"
        >
          No withdrawal network is available for {{ selectedAsset }} right now.
          Please try again shortly.
        </p>
        <p
          v-if="selectedNetwork"
          class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2"
        >
          Only send <b>{{ selectedAsset }}</b> on the
          <b>{{ selectedNetwork }}</b> network to this address. Sending the
          wrong asset or using a different network will result in permanent
          loss of funds.
        </p>
      </div>
      <div class="mt-6">
        <v-text-field
          v-model="amount"
          type="number"
          label="Enter Amount"
          filled
          rounded
          placeholder="Enter amount"
          :hint="`You have ${presentCoinData.balance} ${selectedAsset} in your wallet`"
          persistent-hint
        ></v-text-field>
        <p v-if="amountError" class="text-sm text-red-600 mt-1">
          {{ amountError }}
        </p>
        <div class="d-flex gap-2 mt-2">
          <v-btn
            v-for="pct in [25, 50, 75, 100]"
            :key="pct"
            small
            rounded
            class="flex-grow-1"
            @click="setAmountPercent(pct)"
            >{{ pct }}%</v-btn
          >
        </div>
      </div>
      <div class="mt-6">
        <v-text-field
          v-model="receiverWalletAddress"
          label="Enter Receiver Wallet Address"
          filled
          rounded
          placeholder="Enter receiver wallet address"
        ></v-text-field>
      </div>
      <div class="mt-6">
        <v-textarea
          v-model="note"
          label="Enter Remark"
          filled
          rounded
          placeholder="Enter remark (optional)"
        ></v-textarea>
      </div>

      <div
        v-if="Number(amount) > 0 && !amountError"
        class="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4"
      >
        <div class="flex justify-between text-sm mb-1">
          <span>Amount requested</span>
          <span class="font-medium"
            >{{ Number(amount) }} {{ selectedAsset }}</span
          >
        </div>
        <div class="flex justify-between text-sm mb-1">
          <span>Network fee</span>
          <span class="font-medium">
            <span v-if="loadingFee">Calculating&hellip;</span>
            <span v-else>{{ withdrawalFee }} {{ selectedAsset }}</span>
          </span>
        </div>
        <div class="flex justify-between text-sm font-semibold border-t border-gray-200 pt-2 mt-2">
          <span>You will receive</span>
          <span>{{ amountToReceive }} {{ selectedAsset }}</span>
        </div>
        <div v-if="accountToReceiveBelowZero" class="text-red-600 text-sm mt-2">
          Warning: The network fee exceeds the amount you are trying to withdraw. Please increase the withdrawal amount or choose a different network.
        </div>
        <div v-if="feeError" class="text-red-600 text-sm mt-2">
          {{ feeError }}
        </div>
      </div>

      <div class="mt-6">
        <v-text-field
          v-model="otp"
          filled
          rounded
          label="OTP"
          hint="An OTP will be sent to your registered email to approve this transaction"
          persistent-hint
        >
          <template v-slot:append-outer>
            <v-btn :loading="sendingOtp" @click="generateOtp" color="primary" dark>
              Request OTP
            </v-btn>
          </template>
        </v-text-field>
      </div>

      <div class="mt-6">
        <v-btn
          color="primary"
          @click="withdrawCryptoExternal"
          :loading="loadingWithdraw"
          :disabled="!canSubmitExternalWithdraw || !withdrawIsEnabled || !otp"
          >Withdraw</v-btn
        >
      </div>
    </div>
  </div>
</template>

<script>
import cryptocoin from '../../../../api/users/cryptocoin'
import misc from '../../../../api/users/misc'
import debounce from 'lodash/debounce'
import { formatCryptoAmount } from '../../../../utils/cryptoHistory'

export default {
  data() {
    return {
      selectedAsset: '',
      selectedNetwork: '',
      showAssetModal: false,
      showNetworkModal: false,
      assetSearchQuery: '',
      cryptoAssets: [],
      currentAsset: null,
      coinId: '',
      snappyExchangeUsername: '',
      loadingRecipients: false,
      recipient: null,
      hasSearched: false,
      amount: 0,
      wallets: [],
      presentCoinData: {},
      note: '',
      addTransactionDialog: false,
      transactions: [],
      isEditingIndex: null,
      // case: snappy_exchange_username, external_wallet
      withdrawType: '',
      // type: crypto, fiat
      withdrawAssetType: '',
      loadingWithdraw: false,
      receiverWalletAddress: '',
      withdrawalFee: 0,
      feeError: '',
      loadingFee: false,
      otp: '',
      sendingOtp: false
    }
  },
  computed: {
    numericAmount() {
      const n = Number(this.amount)
      return Number.isFinite(n) ? n : 0
    },
    numericBalance() {
      if (!this.presentCoinData || !this.presentCoinData.balance) return 0
      const n = Number(this.presentCoinData.balance)
      return Number.isFinite(n) ? n : 0
    },
    amountError() {
      if (!this.amount || this.numericAmount <= 0) return ''
      const coin = this.presentCoinData && this.presentCoinData.coin
      if (this.numericAmount > this.numericBalance) {
        return 'Amount exceeds your available balance'
      }
      if (coin && coin.minimumWithdraw && this.numericAmount < coin.minimumWithdraw) {
        return `Minimum withdrawal is ${coin.minimumWithdraw} ${this.selectedAsset}`
      }
      if (coin && coin.maximumWithdraw && this.numericAmount > coin.maximumWithdraw) {
        return `Maximum withdrawal is ${coin.maximumWithdraw} ${this.selectedAsset}`
      }
      return ''
    },
    amountToReceive() {
      const receive = this.numericAmount - Number(this.withdrawalFee || 0)
      return receive;
    },
    accountToReceiveBelowZero() {
      return this.amountToReceive < 0
    },
    canSubmitExternalWithdraw() {
      return (
        !!this.selectedNetwork &&
        !!this.receiverWalletAddress &&
        this.numericAmount > 0 &&
        !this.amountError &&
        !this.loadingFee &&
        !this.feeError
      )
    },
    withdrawIsEnabled() {
      const coin = this.presentCoinData && this.presentCoinData.coin
      return !coin || coin.withdrawEnabled !== false
    },
    // The wallet carries the provider's networks, so only offer the ones
    // withdrawals are currently open on.
    availableNetworks() {
      return (this.presentCoinData?.networks || [])
        .filter((network) => network.withdraws_enabled !== false)
        .map((network) => ({ name: network.name, _id: network.id }))
    }
  },
  mounted() {
    this.coinId = this.$route.params.id
    // The asset-selection modal doesn't pass a `case` query param, so this
    // page previously rendered completely blank (neither branch of the
    // template matched). Default to the external-wallet withdraw form,
    // which is what "Withdraw" is expected to open into.
    const validCases = ['snappy_exchange_username', 'external_wallet']
    this.withdrawType = validCases.includes(this.$route.query.case)
      ? this.$route.query.case
      : 'external_wallet'
    this.withdrawAssetType = this.$route.query.case
    this.getCryptoAssets()
    this.getCoinDetail()
    if (this.withdrawType === 'snappy_exchange_username') {
      this.addTransactionDialog = true
    }
  },
  watch: {
    snappyExchangeUsername() {
      this.recipient = null
      this.hasSearched = false
    },
    amount() {
      this.debouncedFetchFee()
    },
    selectedNetwork() {
      this.debouncedFetchFee()
    },
    // Nothing to choose with a single network - pick it so the withdraw
    // button isn't blocked on a selection the user can't make.
    availableNetworks: {
      immediate: true,
      handler(networks) {
        if (networks.length === 1) {
          this.selectedNetwork = networks[0]._id
        }
      }
    }
  },
  created() {
    this.debouncedFetchFee = debounce(this.fetchWithdrawalFee, 500)
  },
  methods: {
    formatCryptoAmount,
    async getCryptoAssets() {
      try {
        const res = await cryptocoin.cryptoCoin().getCryptosRate()
        const currentCoin = res.data.data.find(
          (asset) => asset.coinId === this.coinId
        )

        if (currentCoin) {
          this.cryptoAssets = [
            {
              symbol: currentCoin.symbol,
              name: currentCoin.currency,
              logo: currentCoin.logo,
              price: currentCoin.price,
              percentageChange: currentCoin.percentageChange,
              coinId: currentCoin.coinId
            }
          ]
          this.selectAsset(this.cryptoAssets[0])
        }
      } catch (error) {
        console.log('Error fetching crypto assets:', error)
      }
    },
    setAmountPercent(pct) {
      this.amount = Number(((this.numericBalance * pct) / 100).toFixed(8))
    },
    selectAsset(asset) {
      this.currentAsset = asset
      this.selectedAsset = asset.symbol
      this.showAssetModal = false
      this.assetSearchQuery = ''
    },
    async recipientSearch() {
      if (!this.snappyExchangeUsername) return
      this.loadingRecipients = true
      const res = await misc.misc().recipientSearch(this.snappyExchangeUsername)
      this.hasSearched = true
      this.recipient = res.error ? null : res.data.data
      this.loadingRecipients = false
    },
    async getCoinDetail() {
      try {
        const res = await cryptocoin.cryptoCoin().getCryptosWallets()
        this.wallets = res.data.data
        this.presentCoinData = res.data.data.find(
          (item) => item.coin._id === this.coinId
        )
      } catch (error) {
        console.log(error)
      }
    },
    onSelectSendToUser() {
      this.withdrawType = 'snappy_exchange_username'
      this.addTransactionDialog = true
    },
    closeTransactionDialog() {
      this.addTransactionDialog = false
      this.isEditingIndex = null
      this.snappyExchangeUsername = ''
      this.recipient = null
      this.hasSearched = false
      this.amount = 0
      this.note = ''

      if (this.transactions.length === 0) {
        this.withdrawType = 'external_wallet'
      }
    },
    async fetchWithdrawalFee() {
      if (
        this.withdrawType !== 'external_wallet' ||
        !this.coinId ||
        !this.selectedNetwork ||
        this.numericAmount <= 0
      ) {
        this.withdrawalFee = 0
        this.feeError = ''
        return
      }
      this.loadingFee = true
      this.feeError = ''
      try {
        const res = await cryptocoin.cryptoCoin().getWithdrawalFee({
          coinId: this.coinId,
          amount: this.numericAmount,
          type: 'external',
          network: this.selectedNetwork
        })
        if (res.error) {
          this.withdrawalFee = 0
          this.feeError =
            (res.errorMessage && res.errorMessage.message) ||
            'Could not calculate the network fee. Please try again.'
          return
        }
        this.withdrawalFee = res.data.data.fee || 0
      } catch (error) {
        this.withdrawalFee = 0
        this.feeError =
          'Could not calculate the network fee. Check your connection and try again.'
      } finally {
        this.loadingFee = false
      }
    },
    async withdrawCrypto() {
      if (!this.withdrawIsEnabled) {
        this.$store.dispatch('alert', {
          message: `Withdrawals are currently disabled for ${this.selectedAsset}`,
          error: true,
          status: true
        })
        return
      }
      this.loadingWithdraw = true
      const res = await cryptocoin.cryptoCoin().sendToUser({
        coinId: this.cryptoAssets[0].coinId,
        transactions: this.transactions.map((transaction) => ({
          receiver: transaction.username,
          amount: Number(transaction.amount),
          remark: transaction.note
        })),
        otp: this.otp
      })
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.errorMessage.message || res.internalError.message,
          error: true,
          status: true
        })
        this.loadingWithdraw = false
        return
      }
      this.$store.dispatch('alert', {
        message: res.message,
        status: true
      })

      this.loadingWithdraw = false
      this.transactions = []
      this.addTransactionDialog = false
      this.isEditingIndex = null
      this.snappyExchangeUsername = ''
      this.recipient = null
      this.amount = 0
      this.note = ''
      this.otp = ''

      this.$router.push('/user/dashboard')
    },
    async withdrawCryptoExternal() {
      if (!this.canSubmitExternalWithdraw) {
        this.$store.dispatch('alert', {
          message:
            this.amountError ||
            'Please select a network, enter an amount and a receiver wallet address',
          error: true,
          status: true
        })
        return
      }
      this.loadingWithdraw = true
      const res = await cryptocoin.cryptoCoin().sendToWallet({
        coinId: this.cryptoAssets[0].coinId,
        network: this.selectedNetwork,
        amount: this.amount,
        receiver: this.receiverWalletAddress,
        remarks: this.note,
        otp: this.otp
      })
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.errorMessage.message || res.internalError.message,
          error: true,
          status: true
        })
        this.loadingWithdraw = false
        return
      }
      this.$store.dispatch('alert', {
        message: res.message,
        status: true
      })

      this.loadingWithdraw = false
      this.selectedNetwork = ''
      this.amount = 0
      this.receiverWalletAddress = ''
      this.note = ''
      this.withdrawalFee = 0
      this.otp = ''

      this.$router.push('/user/dashboard')
    },
    addMoreTransaction() {
      this.isEditingIndex = null
      this.addTransactionDialog = true
    },
    addTransaction() {
      if (this.isEditingIndex !== null) {
        this.transactions[this.isEditingIndex] = {
          username: this.snappyExchangeUsername,
          recipient: this.recipient,
          amount: this.amount,
          note: this.note
        }
      } else {
        this.transactions.push({
          username: this.snappyExchangeUsername,
          recipient: this.recipient,
          amount: this.amount,
          note: this.note
        })
      }
      this.addTransactionDialog = false
    },
    removeTransaction(index) {
      this.transactions.splice(index, 1)
    },
    onEditTransaction(transaction, index) {
      this.isEditingIndex = index
      this.snappyExchangeUsername = transaction.username
      this.recipient = transaction.recipient
      this.amount = transaction.amount
      this.note = transaction.note
      this.addTransactionDialog = true
    },
    async generateOtp() {
      this.sendingOtp = true
      const res = await misc.misc().generateOtp('withdrawal')
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.errorMessage.message || res.internalError.message,
          error: true,
          status: true
        })
        this.sendingOtp = false
        return
      }
      this.$store.dispatch('alert', {
        message: 'OTP sent successfully, kindly check your email',
        status: true
      })
      this.sendingOtp = false
    }
  }
}
</script>

<style scoped>
/* Custom styles for the purple button */
.v-btn--color-purple {
  background-color: #8b5cf6 !important;
  color: white !important;
}

.v-btn--color-purple:hover {
  background-color: #7c3aed !important;
}

.v-btn--color-purple:disabled {
  background-color: #d1d5db !important;
  color: #9ca3af !important;
}
</style>

<style scoped>
/* Custom styles for the purple button */
.v-btn--color-purple {
  background-color: #8b5cf6 !important;
  color: white !important;
}

.v-btn--color-purple:hover {
  background-color: #7c3aed !important;
}

.v-btn--color-purple:disabled {
  background-color: #d1d5db !important;
  color: #9ca3af !important;
}
</style>
